import React, { useState, useEffect } from 'react';
import { DollarSign,IndianRupee, Plus, Minus, RefreshCw, X, Download, FileText } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SummaryApi from '../../../common';


const BankManagementDashboard = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddMoneyModal, setShowAddMoneyModal] = useState(false);
  const [showSpendMoneyModal, setShowSpendMoneyModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    type: '',
    category: '',
    startDate: '',
    endDate: ''
  });
  const [customCreditCategory, setCustomCreditCategory] = useState('');
const [customDebitCategory, setCustomDebitCategory] = useState('');

  // Form states
  const [addMoneyForm, setAddMoneyForm] = useState({
    amount: '',
    category: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  const [spendMoneyForm, setSpendMoneyForm] = useState({
    amount: '',
    category: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  // Categories
  const creditCategories = ['SOFTWARE','SALARY', 'OTHER'];
  const debitCategories = ['EXPENSE', 'WITHDRAWAL','OTHER'];

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTransactions = transactions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(transactions.length / itemsPerPage);

  // Fetch all accounts
  useEffect(() => {
    fetchAccounts();
  }, []);

  // Fetch transactions when account changes
  useEffect(() => {
    if (selectedAccount) {
      fetchTransactions();
    }
  }, [selectedAccount, filters]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, selectedAccount]);

 const fetchAccounts = async () => {
  try {
    const response = await fetch(SummaryApi.bankAccount.getAll.url);
    const data = await response.json();
    if (data.data && data.data.length > 0) {
      setAccounts(data.data);
      // Only set selectedAccount if it's not already set
      if (!selectedAccount) {
        setSelectedAccount(data.data[0]);
      } else {
        // Update the selectedAccount with the latest data while preserving the selection
        const updatedAccount = data.data.find(acc => acc._id === selectedAccount._id);
        if (updatedAccount) {
          setSelectedAccount(updatedAccount);
        }
      }
    }
  } catch (error) {
    console.error("Error fetching accounts:", error);
  }
};

const fetchTransactions = async () => {
  if (!selectedAccount) return;

  setLoading(true);
  try {
    const params = new URLSearchParams({
      bankAccountId: selectedAccount._id,
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== "")),
    }).toString();

    const { url, method } = SummaryApi.bankAccount.getTransactions(selectedAccount._id, params);
    const response = await fetch(url, { method });
    const data = await response.json();
    setTransactions(data.data || []);
  } catch (error) {
    console.error("Error fetching transactions:", error);
  } finally {
    setLoading(false);
  }
};

const handleAddMoney = async (e) => {
  e.preventDefault();
  try {
    const { url, method } = SummaryApi.bankAccount.addMoney(selectedAccount._id);
    const response = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        bankAccountId: selectedAccount._id,
        amount: parseFloat(addMoneyForm.amount),
        category: addMoneyForm.category === 'OTHER' ? customCreditCategory : addMoneyForm.category,
        description: addMoneyForm.description,
        date: addMoneyForm.date,
      }),
    });

    if (response.ok) {
      setShowAddMoneyModal(false);
      setAddMoneyForm({
        amount: "",
        category: "",
        description: "",
        date: new Date().toISOString().split("T")[0],
      });
      setCustomCreditCategory('');
      fetchAccounts();
      fetchTransactions();
    }
  } catch (error) {
    console.error("Error adding money:", error);
  }
};

const handleSpendMoney = async (e) => {
  e.preventDefault();
  try {
    const { url, method } = SummaryApi.bankAccount.spendMoney(selectedAccount._id);
    const response = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        bankAccountId: selectedAccount._id,
        amount: parseFloat(spendMoneyForm.amount),
        category: spendMoneyForm.category === 'OTHER' ? customDebitCategory : spendMoneyForm.category,
        description: spendMoneyForm.description,
        date: spendMoneyForm.date,
      }),
    });

    const data = await response.json();

    if (response.ok) {
      setShowSpendMoneyModal(false);
      setSpendMoneyForm({
        amount: "",
        category: "",
        description: "",
        date: new Date().toISOString().split("T")[0],
      });
      setCustomDebitCategory('');
      fetchAccounts();
      fetchTransactions();
    } else {
      alert(data.message || "Error spending money");
    }
  } catch (error) {
    console.error("Error spending money:", error);
  }
};


  const downloadIndividualPDF = (transaction) => {
    const doc = new jsPDF();

    const cleanText = (text) =>
      text?.replace(/[^\x00-\x7F]/g, '').replace(/\s+/g, ' ').trim() || 'N/A';

    // Header
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('Individual Library-Transaction', 105, 22, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(255, 0, 0);
    
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);

    doc.setLineWidth(0.3);
    doc.line(10, 36, 200, 36);

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Transaction Details', 105, 45, { align: 'center' });

    // Transaction details table
    const date = transaction.date ? new Date(transaction.date).toLocaleDateString('en-GB') : 'N/A';
    
    autoTable(doc, {
      startY: 55,
      head: [['Field', 'Details']],
      body: [
        ['Date', date],
        ['Type', transaction.type || 'N/A'],
        ['Category', transaction.category || 'N/A'],
        ['Description', cleanText(transaction.description)],
        ['Amount', `Rs ${transaction.amount?.toLocaleString() || 0}`],
        ['Balance After', `Rs ${transaction.balanceAfter?.toLocaleString() || 0}`],
      ],
      styles: {
        fontSize: 11,
        cellPadding: 5,
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50 },
        1: { cellWidth: 130 },
      },
      headStyles: {
        fillColor: [79, 70, 229],
      },
    });

    // Principal signature
    const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 30 : 120;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);

    doc.save(`Transaction_${transaction.type}_${date}.pdf`);
  };

  const downloadAllTransactionsPDF = () => {
    const doc = new jsPDF();

    const cleanText = (text) =>
      text?.replace(/[^\x00-\x7F]/g, '').replace(/\s+/g, ' ').trim() || '';

    const sortedTransactions = [...transactions].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    const firstDate = sortedTransactions[0]?.date
      ? new Date(sortedTransactions[0].date).toLocaleDateString('en-GB')
      : '';
    const lastDate = sortedTransactions[sortedTransactions.length - 1]?.date
      ? new Date(sortedTransactions[sortedTransactions.length - 1].date).toLocaleDateString('en-GB')
      : '';

    // Header
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('Library-Transactions', 105, 22, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(255, 0, 0);
    
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);

    doc.setLineWidth(0.3);
    doc.line(10, 36, 200, 36);

    // Bank Account Details
    doc.setFontSize(11);
    let y = 43;
    const leftX = 14;
    const rightX = 110;

    doc.setFont('helvetica', 'bold');
    doc.text('Bank Account Details', 105, y, { align: 'center' });
    y += 6;

    // Row 1
    doc.setFont('helvetica', 'bold');
    doc.text('Account Name:', leftX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(selectedAccount.accountName || '—', leftX + 40, y);

    doc.setFont('helvetica', 'bold');
    doc.text('Account Number:', rightX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(selectedAccount.accountNumber || '—', rightX + 40, y);

    // Row 2
    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('IFSC Code:', leftX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(selectedAccount.ifscCode || '—', leftX + 40, y);

    doc.setFont('helvetica', 'bold');
    doc.text('Account Type:', rightX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(selectedAccount.accountType || '—', rightX + 40, y);

    // Row 3
    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('Current Balance:', leftX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(`Rs ${selectedAccount.balance.toLocaleString()}`, leftX + 40, y);

    y += 4;
    doc.setLineWidth(0.3);
    doc.line(10, y + 2, 200, y + 2);

    // Report Title
    y += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(`Transaction Report (${firstDate} - ${lastDate})`, 105, y, { align: 'center' });

    // Transaction Table
    const tableData = sortedTransactions.map((tx) => [
      new Date(tx.date).toLocaleDateString('en-GB'),
      tx.type,
      tx.category,
      cleanText(tx.description),
      `Rs ${tx.amount.toLocaleString()}`,
      `Rs ${tx.balanceAfter.toLocaleString()}`,
    ]);

    autoTable(doc, {
      startY: y + 8,
      head: [['Date', 'Type', 'Category', 'Description', 'Amount', 'Balance']],
      body: tableData,
      styles: {
        fontSize: 8,
        cellPadding: 3,
        halign: 'center',
        valign: 'middle',
      },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 22 },
        2: { cellWidth: 30 },
        3: { cellWidth: 65, halign: 'left' },
        4: { cellWidth: 25, halign: 'right' },
        5: { cellWidth: 25, halign: 'right' },
      },
      headStyles: {
        fillColor: [79, 70, 229],
      },
    });

    // Totals Section
    const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 180;
    doc.setFontSize(12);

    const totalCredit = sortedTransactions
      .filter((tx) => tx.type === 'CREDIT')
      .reduce((acc, tx) => acc + tx.amount, 0);

    const totalDebit = sortedTransactions
      .filter((tx) => tx.type === 'DEBIT')
      .reduce((acc, tx) => acc + tx.amount, 0);

    // Total Credit
    doc.setTextColor(34, 139, 34);
    doc.setFont('helvetica', 'normal');
    doc.text('Total Credit: ', 14, finalY);
    doc.setFont('helvetica', 'bold');
    doc.text(`Rs ${totalCredit.toLocaleString()}`, 45, finalY);

    // Total Debit
    doc.setTextColor(220, 20, 60);
    doc.setFont('helvetica', 'normal');
    doc.text('Total Debit: ', 14, finalY + 7);
    doc.setFont('helvetica', 'bold');
    doc.text(`Rs ${totalDebit.toLocaleString()}`, 45, finalY + 7);

    // Available Balance
    doc.setTextColor(30, 144, 255);
    doc.setFont('helvetica', 'normal');
    doc.text('Available Balance: ', 14, finalY + 14);
    doc.setFont('helvetica', 'bold');
    doc.text(`Rs ${selectedAccount.balance.toLocaleString()}`, 60, finalY + 14);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);

    doc.save(`Transaction_Report_${selectedAccount.accountName}_${new Date().toLocaleDateString('en-GB')}.pdf`);
  };

  const formatCurrency = (amount) => {
    return `Rs ${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  if (!selectedAccount) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-800">Bank Account Management</h1>
          
          {/* Account Selector */}
          <div className="flex items-center gap-2">
            <IndianRupee className="text-blue-600" />
            <select
              value={selectedAccount._id}
              onChange={(e) => {
                const account = accounts.find(acc => acc._id === e.target.value);
                setSelectedAccount(account);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              {accounts.map(account => (
                <option key={account._id} value={account._id}>
                  {account.accountName} - {account.accountNumber}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Balance Card and Action Buttons */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Balance Card */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-600 mb-2">Current Bank Balance</h2>
            <p className="text-4xl font-bold text-green-600 mb-2">
              {formatCurrency(selectedAccount.balance)}
            </p>
            <p className="text-sm text-gray-500">
              Account: {selectedAccount.accountName} ({selectedAccount.accountType})
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Account Number: {selectedAccount.accountNumber}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-4">
            <button
              onClick={() => setShowAddMoneyModal(true)}
              className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              <Plus size={20} />
              Add Money
            </button>
            <button
              onClick={() => setShowSpendMoneyModal(true)}
              className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              <Minus size={20} />
              Spend Money
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="CREDIT">Credit</option>
              <option value="DEBIT">Debit</option>
            </select>

            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {[...creditCategories, ...debitCategories].map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={fetchTransactions}
            className="mt-4 flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <RefreshCw size={16} />
            Refresh Table
          </button>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-blue-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Sr. No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                      Loading transactions...
                    </td>
                  </tr>
                ) : currentTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                      No transactions found
                    </td>
                  </tr>
                ) : (
                  currentTransactions.map((tx, index) => (
                    <tr key={tx._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {indexOfFirstItem + index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {tx.formattedDate || new Date(tx.date).toLocaleDateString('en-GB')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          tx.type === 'CREDIT' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {tx.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tx.category}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(tx.amount)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{tx.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => downloadIndividualPDF(tx)}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          title="Download PDF"
                        >
                          <Download size={16} />
                          PDF
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(indexOfLastItem, transactions.length)}</span> of{' '}
                    <span className="font-medium">{transactions.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {[...Array(totalPages)].map((_, i) => {
                      const pageNum = i + 1;
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => paginate(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-blue-600 border-blue-600 text-white'
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                        return (
                          <span
                            key={pageNum}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Download All Transactions Button */}
        {transactions.length > 0 && (
          <div className="flex justify-center mb-6">
            <button
              onClick={downloadAllTransactionsPDF}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors shadow-md"
            >
              <FileText size={20} />
              Download Complete Transaction Report
            </button>
          </div>
        )}
      </div>

      {/* Add Money Modal */}
      {showAddMoneyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Add Money to Account</h3>
              <button onClick={() => setShowAddMoneyModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Selected Account: {selectedAccount.accountName}<br />
              <span className="text-xs text-gray-500">{selectedAccount.accountType}</span>
            </p>
            <div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (Rs)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={addMoneyForm.amount}
                    onChange={(e) => setAddMoneyForm({ ...addMoneyForm, amount: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter amount"
                  />
                </div>
                <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Select Category</label>
  <select
    required
    value={addMoneyForm.category}
    onChange={(e) => setAddMoneyForm({ ...addMoneyForm, category: e.target.value })}
    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
  >
    <option value="">Choose category</option>
    {creditCategories.map(cat => (
      <option key={cat} value={cat}>{cat}</option>
    ))}
  </select>
</div>
{addMoneyForm.category === 'OTHER' && (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">Enter Custom Category</label>
    <input
      type="text"
      required
      value={customCreditCategory}
      onChange={(e) => setCustomCreditCategory(e.target.value)}
      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
      placeholder="Enter category name"
    />
  </div>
)}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={addMoneyForm.description}
                    onChange={(e) => setAddMoneyForm({ ...addMoneyForm, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter description"
                    rows="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={addMoneyForm.date}
                    onChange={(e) => setAddMoneyForm({ ...addMoneyForm, date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddMoneyModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddMoney}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Add Money
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spend Money Modal */}
      {showSpendMoneyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Spend Money from Account</h3>
              <button onClick={() => setShowSpendMoneyModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Selected Account: {selectedAccount.accountName}<br />
              <span className="text-xs text-gray-500">{selectedAccount.accountType}</span>
            </p>
            <div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (Rs)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={spendMoneyForm.amount}
                    onChange={(e) => setSpendMoneyForm({ ...spendMoneyForm, amount: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Enter amount"
                  />
                </div>
                <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">Select Category</label>
  <select
    required
    value={spendMoneyForm.category}
    onChange={(e) => setSpendMoneyForm({ ...spendMoneyForm, category: e.target.value })}
    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
  >
    <option value="">Choose category</option>
    {debitCategories.map(cat => (
      <option key={cat} value={cat}>{cat}</option>
    ))}
  </select>
</div>
{spendMoneyForm.category === 'OTHER' && (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">Enter Custom Category</label>
    <input
      type="text"
      required
      value={customDebitCategory}
      onChange={(e) => setCustomDebitCategory(e.target.value)}
      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
      placeholder="Enter category name"
    />
  </div>
)}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={spendMoneyForm.description}
                    onChange={(e) => setSpendMoneyForm({ ...spendMoneyForm, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Enter description"
                    rows="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={spendMoneyForm.date}
                    onChange={(e) => setSpendMoneyForm({ ...spendMoneyForm, date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowSpendMoneyModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSpendMoney}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Spend Money
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankManagementDashboard;